import * as React from 'react';
import { colors } from '@alixpartners/ui-components';
import { renderToString } from 'react-dom/server';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import {
	TooltipColor,
	TooltipContainer,
	TooltipItem,
	TooltipItemName,
	TooltipItemValue,
	Wrapper,
	Title,
} from './SummaryBarChart.styles';
import { getFormattedCurrencyWithIdealDenomination } from 'utils/number';

const defaultOptions = {
	chart: {
		height: 160,
		width: 360,
		type: 'column',
	},
	credits: {
		enabled: false,
	},
	exporting: {
		enabled: false,
	},
	legend: {
		align: 'right',
		itemStyle: {
			color: colors.grey5,
			fontSize: '11px',
			fontWeight: 'normal',
		},
		symbolRadius: 0,
		verticalAlign: 'top',
		x: 16,
	},
	plotOptions: {
		column: {
			borderWidth: 0,
			groupPadding: 0.33,
			pointPadding: 0,
			pointWidth: 4,
		},
	},
	series: [],
	title: {
		text: '',
	},
	tooltip: {
		shared: true,
		useHTML: true,
	},
	xAxis: {
		labels: {
			step: 1,
			style: {
				color: colors.grey5,
				fontSize: '11px',
			},
		},
	},
	yAxis: {
		max: 100,
		min: 0,
		tickAmount: 5,
		title: {
			text: '',
		},
	},
};

export const SummaryBarChart = ({ data, currencySymbol }) => {
	const currentPeriodData = data.map(item => item.values[0]);
	const prevPeriodData = data.map(item => item.values[1] || 0);
	const yAxisMax = Math.max(...currentPeriodData, ...prevPeriodData);
	const yAxisMin = Math.min(...currentPeriodData, ...prevPeriodData);

	const currentSeries = {
		color: colors.chartDarkGrey,
		data: currentPeriodData,
		marker: {
			enabled: false,
		},
		name: 'Before',
	};
	const prevSeries = {
		color: colors.amber,
		data: prevPeriodData,
		marker: {
			enabled: false,
		},
		name: 'After',
	};

	const hasPrevSeries = prevPeriodData.filter(Boolean).length > 0;
	const options = {
		...defaultOptions,
		chart: {
			...defaultOptions.chart,
			type: 'column',
		},
		series: hasPrevSeries ? [currentSeries, prevSeries] : [currentSeries],
		tooltip: {
			...defaultOptions.tooltip,
			shared: true,
			useHTML: true,
			backgroundColor: colors.white,
			formatter() {
				const tooltip = this;
				const points = tooltip.points || [];

				return renderToString(
					<TooltipContainer>
						{points.map(
							point =>
								point ? (
									<TooltipItem key={`${point.series.name}-${point.color}`}>
										<span>
											<TooltipColor color={point.color} />
											<TooltipItemName>{point.series.name}</TooltipItemName>
										</span>
										<TooltipItemValue>
											{getFormattedPrice(point.y, currencySymbol)}
										</TooltipItemValue>
									</TooltipItem>
								) : null
						)}
					</TooltipContainer>
				);
			},
		},
		xAxis: {
			...defaultOptions.xAxis,
			categories: data.map(item => item.label),
		},
		yAxis: {
			...defaultOptions.yAxis,
			endOnTick: true,
			labels: {
				formatter() {
					const yAxis = this;

					return getFormattedPrice(yAxis.value, currencySymbol);
				},
				style: {
					color: colors.grey5,
					fontSize: '11px',
				},
			},
			max: yAxisMax,
			min: yAxisMin,
			startOnTick: true,
		},
	};

	return (
		<Wrapper>
			<Title>Spend per DPO</Title>
			<HighchartsReact highcharts={Highcharts} options={options} />
		</Wrapper>
	);
};

export const getFormattedPrice = (value, currencySymbol) => {
	let formattedPrice = getFormattedCurrencyWithIdealDenomination({
		value,
		isUnitSign: true,
		hideDecimals: true,
	});

	formattedPrice = formattedPrice
		? `${currencySymbol}${formattedPrice}`
		: formattedPrice;

	return formattedPrice;
};
